export type ReadingSpeedId = 'slow' | 'average' | 'fast' | 'aloud';

export type ReadingSpeed = {
  id: ReadingSpeedId;
  label: string;
  wpm: number;
};

export type TextStats = {
  words: number;
  chars: number;
  charsNoSpaces: number;
};

export type DocStatRecord = {
  wordCount: number;
  charCount: number;
  charCountNoSpaces: number;
};

export type DocStatsTotals = TextStats & {
  docs: number;
};

export type NoteContentStat = TextStats;

/** Midpoints of typical ranges; ranges themselves are not shown in UI. */
export const READING_SPEEDS: readonly ReadingSpeed[] = [
  { id: 'slow', label: 'Slow', wpm: 125 },
  { id: 'average', label: 'Average', wpm: 225 },
  { id: 'fast', label: 'Fast', wpm: 350 },
  { id: 'aloud', label: 'Aloud', wpm: 155 },
];

export const DEFAULT_WORDS_PER_PAGE = 300;

export function countWords(text: string): number {
  const trimmed = String(text || '').trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).filter(Boolean).length;
}

export function normalizeWordsPerPage(value: unknown): number {
  const n = Number.parseInt(String(value ?? ''), 10);
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_WORDS_PER_PAGE;
  return n;
}

export function pageCount(
  wordCount: number,
  wordsPerPage: number = DEFAULT_WORDS_PER_PAGE,
): number {
  const words = Math.max(0, Number(wordCount) || 0);
  const per = normalizeWordsPerPage(wordsPerPage);
  if (words === 0) return 0;
  return words / per;
}

export function formatPageCount(
  wordCount: number,
  wordsPerPage: number = DEFAULT_WORDS_PER_PAGE,
): string {
  const pages = pageCount(wordCount, wordsPerPage);
  if (pages === 0) return '0';
  if (Number.isInteger(pages)) return pages.toLocaleString();
  return pages.toLocaleString(undefined, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  });
}

export function countCharacters(
  text: string,
  { spaces = true }: { spaces?: boolean } = {},
): number {
  const raw = String(text || '');
  if (spaces) return raw.length;
  return raw.replace(/\s/g, '').length;
}

export function textStats(text: string): TextStats {
  const normalized = String(text || '').trim();
  return {
    words: countWords(normalized),
    chars: countCharacters(normalized),
    charsNoSpaces: countCharacters(normalized, { spaces: false }),
  };
}

export function readingMinutes(wordCount: number, wpm: number): number {
  const words = Math.max(0, Number(wordCount) || 0);
  const rate = Math.max(1, Number(wpm) || 1);
  if (words === 0) return 0;
  return words / rate;
}

export function formatReadingDuration(minutes: number): string {
  const m = Math.max(0, Number(minutes) || 0);
  if (m < 1) {
    const secs = Math.round(m * 60);
    if (secs <= 0) return '—';
    return `${secs}s`;
  }
  if (m < 60) {
    const whole = Math.floor(m);
    const secs = Math.round((m - whole) * 60);
    if (secs === 0) return `${whole} min`;
    return `${whole}m ${secs}s`;
  }
  const hours = Math.floor(m / 60);
  const mins = Math.round(m % 60);
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

export function averageReadingDuration(wordCount: number): string {
  const average =
    READING_SPEEDS.find((s) => s.id === 'average') ?? READING_SPEEDS[1]!;
  return formatReadingDuration(readingMinutes(wordCount, average.wpm));
}

export type ReadingEstimate = ReadingSpeed & {
  minutes: number;
  labelDuration: string;
};

export function readingEstimates(wordCount: number): ReadingEstimate[] {
  return READING_SPEEDS.map((speed) => ({
    ...speed,
    minutes: readingMinutes(wordCount, speed.wpm),
    labelDuration: formatReadingDuration(readingMinutes(wordCount, speed.wpm)),
  }));
}

export function avgWordsPerDoc(wordCount: number, docs: number): number {
  const words = Math.max(0, Number(wordCount) || 0);
  const count = Math.max(0, Number(docs) || 0);
  if (count === 0) return 0;
  return Math.round(words / count);
}

export function sumDocStats(
  items: Iterable<DocStatRecord> = [],
): DocStatsTotals {
  let words = 0;
  let chars = 0;
  let charsNoSpaces = 0;
  let docs = 0;
  for (const item of items) {
    words += Number(item.wordCount) || 0;
    chars += Number(item.charCount) || 0;
    charsNoSpaces += Number(item.charCountNoSpaces) || 0;
    docs += 1;
  }
  return { words, chars, charsNoSpaces, docs };
}

/**
 * Merges per-note content stats with unsaved bodies and an optional live
 * override for the active note (ignores a live zero when stored words > 0).
 */
export function mergeWorkspaceDocTotals({
  contentStats,
  unsavedBodies,
  activeWsPath,
  activeDocStats,
}: {
  contentStats: ReadonlyMap<string, NoteContentStat>;
  unsavedBodies?: ReadonlyMap<string, string>;
  activeWsPath?: string | null;
  activeDocStats?: TextStats | null;
}): DocStatsTotals {
  const byPath = new Map<string, DocStatRecord>();

  for (const [wsPath, stats] of contentStats) {
    byPath.set(wsPath, {
      wordCount: stats.words,
      charCount: stats.chars,
      charCountNoSpaces: stats.charsNoSpaces,
    });
  }

  if (unsavedBodies) {
    for (const [wsPath, body] of unsavedBodies) {
      const stats = textStats(body);
      byPath.set(wsPath, {
        wordCount: stats.words,
        charCount: stats.chars,
        charCountNoSpaces: stats.charsNoSpaces,
      });
    }
  }

  if (activeWsPath && activeDocStats) {
    const stored = byPath.get(activeWsPath);
    const storedWords = stored?.wordCount ?? 0;
    if (!(activeDocStats.words === 0 && storedWords > 0)) {
      byPath.set(activeWsPath, {
        wordCount: activeDocStats.words,
        charCount: activeDocStats.chars,
        charCountNoSpaces: activeDocStats.charsNoSpaces,
      });
    }
  }

  return sumDocStats(byPath.values());
}
